import React, { useState, useEffect } from 'react';
import { airtableService } from '../services/airtable';

const TableStatusPanel = () => {
  const [tableStatus, setTableStatus] = useState({});
  const [existingSessions, setExistingSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);

  const checkTables = async () => {
    setLoading(true);
    try {
      const status = await airtableService.listAllTables();
      setTableStatus(status);
      
      // Try to get existing sessions if Sessions table is accessible
      if (status.Sessions?.accessible) {
        const sessions = await airtableService.getAllSessions();
        setExistingSessions(sessions);
      }
    } catch (error) {
      console.error('Error checking tables:', error);
    } finally {
      setLoading(false);
    }
  };

  const createMissingTables = async () => {
    setCreating(true);
    console.log('🔨 Starting table creation process...');
    
    try {
      const results = await airtableService.setupTablesIfNeeded();
      console.log('Table creation results:', results);
      
      // Refresh table status
      await checkTables();
      
      if (results) {
        alert('Tables created successfully! You can now start a session.');
      } else {
        alert('Table creation failed. You may need to create tables manually in Airtable.');
      }
    } catch (error) {
      console.error('Table creation error:', error);
      alert(`Table creation failed: ${error.message}`);
    } finally {
      setCreating(false);
    }
  };

  useEffect(() => {
    if (airtableService.isInitialized) {
      checkTables();
    }
  }, []);

  const StatusIcon = ({ status }) => {
    if (!status) return '❓';
    if (status.accessible) return '✅';
    if (status.exists && !status.accessible) return '🔒';
    return '❌';
  };

  const formatTime = (timeString) => {
    return new Date(timeString).toLocaleString();
  };

  return (
    <div style={{
      border: '2px solid var(--light-blue)',
      borderRadius: '8px',
      padding: '16px',
      marginBottom: '20px',
      backgroundColor: 'var(--warm-cream)'
    }}>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '16px'
      }}>
        <h3 style={{ margin: 0, color: 'var(--text-dark)' }}>
          🗄️ Database Status
        </h3>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button 
            onClick={checkTables}
            disabled={loading}
            className="btn btn-primary"
            style={{ fontSize: '12px', padding: '6px 12px' }}
          >
            {loading ? 'Checking...' : 'Refresh'}
          </button>
          
          <button 
            onClick={createMissingTables}
            disabled={creating || loading}
            className="btn btn-success"
            style={{ fontSize: '12px', padding: '6px 12px' }}
          >
            {creating ? 'Creating...' : 'Create Tables'}
          </button>
        </div>
      </div>

      <div style={{ marginBottom: '16px' }}>
        <strong style={{ display: 'block', marginBottom: '8px' }}>Required Tables:</strong>
        {Object.entries(tableStatus).map(([tableName, status]) => (
          <div key={tableName} style={{ 
            display: 'flex', 
            alignItems: 'center', 
            marginBottom: '4px',
            fontSize: '14px'
          }}>
            <StatusIcon status={status} />
            <span style={{ marginLeft: '8px', minWidth: '100px' }}>{tableName}</span>
            <span style={{ color: 'var(--text-light)' }}>
              {status?.accessible ? 'Ready' : 
               status?.exists ? 'No Access' : 
               status?.error || 'Not Found'}
            </span>
          </div>
        ))}
      </div>

      {existingSessions.length > 0 && (
        <div>
          <strong style={{ display: 'block', marginBottom: '8px' }}>Recent Sessions:</strong>
          <div style={{ maxHeight: '150px', overflowY: 'auto' }}>
            {existingSessions.map((session) => (
              <div key={session.id} style={{
                padding: '8px',
                marginBottom: '4px',
                backgroundColor: 'white',
                borderRadius: '4px',
                fontSize: '12px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <div>
                  <div><strong>Code:</strong> {session.fields['Access Code']} | <strong>Clinic:</strong> {session.fields['Clinic Code']}</div>
                  <div style={{ color: 'var(--text-light)' }}>
                    {formatTime(session.fields['Start Time'])}
                  </div>
                </div>
                <span style={{
                  padding: '2px 6px',
                  borderRadius: '4px',
                  backgroundColor: session.fields['Status'] === 'active' ? 'var(--muted-sage)' : 'var(--text-light)',
                  color: 'white',
                  fontSize: '10px'
                }}>
                  {session.fields['Status']}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {Object.keys(tableStatus).length > 0 && !Object.values(tableStatus).every(s => s.accessible) && (
        <div style={{
          marginTop: '12px',
          padding: '8px',
          backgroundColor: '#ffe6e6',
          borderRadius: '4px',
          fontSize: '12px'
        }}>
          <strong>⚠️ Setup Required:</strong> Some tables are missing or inaccessible. 
          Click "Create Tables" or create them manually in Airtable.
        </div>
      )}
    </div>
  );
};

export default TableStatusPanel;